import React from 'react'

interface SchemaMarkupProps {
  type?: 'Organization' | 'WebSite' | 'Service' | 'LocalBusiness' | 'FAQPage' | 'BreadcrumbList'
  data?: any
}

/**
 * The canonical Dimension Organization entity.
 *
 * Rendered once site-wide from _document.tsx. Individual pages must NOT also
 * emit `<SchemaMarkup type="Organization" />` — Organization describes the
 * company, not the page, so repeating it per route is redundant bytes on every
 * response and two copies to keep in sync.
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Dimension",
  "alternateName": "Dimension Waste Management",
  "url": "https://getdimension.com",
  "logo": "https://getdimension.com/_next/static/media/dimension-logo.a93cba67.svg",
  "description": "Dimension is the all-in-one solution for waste management, nationwide. We provide simple and sustainable waste management solutions for homes and businesses across the country.",
  "foundingDate": "2020",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2261 Market Street, #4061",
    "addressLocality": "San Francisco",
    "addressRegion": "CA",
    "postalCode": "94114",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1 (415) 340-7182",
    "contactType": "customer service",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://www.facebook.com/getdimension",
    "https://www.linkedin.com/company/dimension-waste-management",
    "https://twitter.com/getdimension"
  ],
  "serviceArea": {
    "@type": "Country",
    "name": "United States"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Waste Management Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Junk Removal",
          "description": "Professional junk removal services for homes and businesses"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Dumpster Rental",
          "description": "Affordable dumpster rental services for short and long term needs"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Hazardous Waste Disposal",
          "description": "Safe and eco-friendly hazardous waste disposal services"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Cardboard Recycling",
          "description": "Professional cardboard recycling and disposal services"
        }
      }
    ]
  }
})

const SchemaMarkup: React.FC<SchemaMarkupProps> = ({ type = 'Organization', data }) => {
  const getWebSiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Dimension",
    "url": "https://getdimension.com",
    "description": "Dimension is the all-in-one solution for waste management, nationwide.",
    "publisher": {
      "@type": "Organization",
      "name": "Dimension"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://getdimension.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  })

  const getServiceSchema = () => {
    const schema: any = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": data?.name || "Waste Management Services",
      "description": data?.description || "Professional waste management and disposal services for homes and businesses",
      "provider": data?.provider ? {
        "@type": "Organization",
        "name": data.provider.name,
        "url": data.provider.url,
        "address": {
          "@type": "PostalAddress",
          ...data.provider.address,
        },
        "telephone": data.provider.telephone,
      } : {
        "@type": "Organization",
        "name": "Dimension"
      },
      // City/location pages pass their own areaServed (e.g. a schema.org City);
      // everything else is a nationwide service.
      "areaServed": data?.areaServed || {
        "@type": "Country",
        "name": "United States"
      },
      "serviceType": data?.serviceType || "Waste Management",
      "availableChannel": {
        "@type": "ServiceChannel",
        "name": "Dimension Waste Management",
        "serviceUrl": data?.url || "https://getdimension.com"
      }
    }
    // Offers are names only. We deliberately publish no prices: quoting is the
    // sales team's job, and a published figure a customer's quote then misses
    // creates friction rather than trust.
    if (data?.offers) {
      schema.hasOfferCatalog = {
        "@type": "OfferCatalog",
        "name": `${data.name} Options`,
        "itemListElement": data.offers.map((offer: { name: string }) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": offer.name,
          }
        }))
      }
    }

    return schema
  }

  const getFAQPageSchema = () => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (data?.questions || []).map((q: { question: string; answer: string }) => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  })

  const getLocalBusinessSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Dimension",
    "description": "Professional waste management services for homes and businesses",
    "url": "https://getdimension.com",
    "telephone": "+1-800-DIMENSION",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2261 Market Street, #4061",
      "addressLocality": "San Francisco",
      "addressRegion": "CA",
      "postalCode": "94114",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "37.7749",
      "longitude": "-122.4194",
    },
    "openingHours": "Mo-Su 08:00-20:00",
    "priceRange": "$$",
    "serviceArea": {
      "@type": "Country",
      "name": "United States"
    }
  })

  // Breadcrumbs give an engine the service hierarchy explicitly instead of
  // making it infer one from the URL. Pass items ordered root-first.
  const getBreadcrumbSchema = () => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": (data?.items || []).map(
      (item: { name: string; url: string }, index: number) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url,
      })
    ),
  })

  const getSchemaData = () => {
    switch (type) {
      case 'WebSite':
        return getWebSiteSchema()
      case 'Service':
        return getServiceSchema()
      case 'FAQPage':
        return getFAQPageSchema()
      case 'LocalBusiness':
        return getLocalBusinessSchema()
      case 'BreadcrumbList':
        return getBreadcrumbSchema()
      default:
        return getOrganizationSchema()
    }
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(getSchemaData(), null, 2)
      }}
    />
  )
}

export default SchemaMarkup
